/* Program Name: Window.js
 **  Description: a container class for any modal windows within the application
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-09-06  STT  Initial program
 *********************************************************************************/

/** React imports */
import React from 'react'
import { useTheme } from '@emotion/react'

/** MUI imports */
import { Dialog, DialogActions, Paper } from '@mui/material'
import { Close } from '@mui/icons-material'

/**ezPOP imports */
import { PrimaryButton, SuccessButton, ErrorButton } from './ActionColorButtons'
import { ModalDivH90 } from './CustomDivs'

/** Draggable */
import Draggable from 'react-draggable'

/******************************************************************************/
/**Paper Component
 * setup for the draggable component to make windows draggable
 */
function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds={'parent'}
    >
      <Paper {...props} />
    </Draggable>
  )
}

/** Window
 * component container for content that will be displayed in a modal window for users
 * @param {boolean} open - controls wether the window is open or not
 * @param {Function} close - function that fires on the window closing
 * @param {String} title - title of the window
 * @param {Boolean} noContainer - if true the window will not use the stylized container
 * @param {Function} handleSuccess - function that fires onClick of success button
 * @param {Function} handlePrimary - function that fires onClick of primary button
 * @param {Function} handleError - function that fires onClick of error button
 * @param {String} width - controls the max width of the window 'xs','sm','md'.'lg','xl'
 * @param {boolean} noButtons - if true the window will have no buttons
 * @param {JSX} children - everything that is to be displayed within this component
 * @param {String} success - text for the success button
 * @param {String} priamry - text for the primary button
 * @param {String} error - text for the error button
 * @param {String} success2 - text for the 2nd success button
 * @param {String} handleSuccess2 - function that fires onClick of 2nd success button
 * @param {String} flexWidth - if false window with is 100%
 */
const Window = ({
  open,
  close,
  title,
  noContainer,
  handleSuccess,
  handlePrimary,
  handleError,
  width,
  noButtons,
  children,
  success,
  primary,
  error,
  success2,
  handleSuccess2,
  flexWidth,
}) => {
  /******************************************************************************/
  /**State Variables*/

  //gets the current theme info for the app
  const theme = useTheme()

  /******************************************************************************/
  /**Rendering */
  return (
    <Dialog
      PaperProps={{ style: { overflowY: 'visible' } }}
      open={open}
      onClose={() => {
        close()
      }}
      fullWidth={flexWidth ? false : true}
      maxWidth={width}
      PaperComponent={PaperComponent}
    >
      <div
        id="draggable-dialog-title"
        style={{
          display: 'flex',
          cursor: 'move',
          //backgroundColor: theme.palette.primary.main,
          backgroundRepeat: 'no-repeat',
          background:
            'linear-gradient(to right,' +
            theme.palette.primary.main +
            '4D, ' +
            theme.palette.secondary.main +
            '4D), ' +
            'linear-gradient(45deg, ' +
            theme.palette.primary.main +
            '99 20%, ' +
            theme.palette.primary.main +
            'B3 20%, ' +
            theme.palette.primary.main +
            'B3 40%, ' +
            theme.palette.primary.main +
            'CC 40%, ' +
            theme.palette.primary.main +
            'CC 60%, ' +
            theme.palette.primary.main +
            'E6 60%, ' +
            theme.palette.primary.main +
            'E6 80%, ' +
            theme.palette.primary.main +
            'FF 80%), ' +
            'linear-gradient(-45deg, rgba(91, 91, 91, .1) 20%, ' +
            'rgba(91, 91, 91,.2) 20%, ' +
            'rgba(91, 91, 91,.2) 40%, ' +
            'rgba(91, 91, 91,.4) 40%, ' +
            'rgba(91, 91, 91,.4) 60%, ' +
            'rgba(91, 91, 91,.6) 60%, ' +
            'rgba(91, 91, 91,.6) 80%, ' +
            'rgba(91, 91, 91,.8) 80%), ' +
            'linear-gradient(90deg, rgba(122,122,122,0) 20%, ' +
            'rgba(122,122,122,.3) 20%, ' +
            'rgba(122,122,122,.3) 40%, ' +
            'rgba(122,122,122,.5) 40%, ' +
            'rgba(122,122,122,.5) 60%, ' +
            'rgba(122,122,122,.7) 60%, ' +
            'rgba(122,122,122,.7) 80%, ' +
            'rgba(122,122,122,.8) 80%), ' +
            'linear-gradient(-90deg, ' +
            theme.palette.secondary.main +
            '80 20%, ' +
            theme.palette.secondary.main +
            '99 20%, ' +
            theme.palette.secondary.main +
            '99 40%, ' +
            theme.palette.secondary.main +
            'B3 40%, ' +
            theme.palette.secondary.main +
            'B3 60%, ' +
            theme.palette.secondary.main +
            'CC 60%, ' +
            theme.palette.secondary.main +
            'CC 80%, ' +
            theme.palette.secondary.main +
            'E6 80%)',
          backgroundSize: '100% 100%',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
        }}
      >
        <div style={{ flex: 1 }}>
          <span
            style={{
              margin: 8,
              fontSize: 24,
              fontWeight: 600,
              color: theme.palette.getContrastText(theme.palette.primary.main),
            }}
          >
            {title}
          </span>
        </div>
        <div
          style={{
            cursor: 'pointer',
            margin: 8,
            height: 24,
            width: 24,
            borderRadius: 50,
            '&:hover': {
              backgroundColor: '#d4d4d4',
            },
          }}
          onClick={() => {
            close()
          }}
        >
          <Close
            sx={{
              fontSize: 24,
              color: theme.palette.getContrastText(theme.palette.primary.main),
            }}
          />
        </div>
      </div>
      <ModalDivH90 style={{ padding: 15 }}>
        {noContainer ? <div>{children}</div> : <div>{children}</div>}
      </ModalDivH90>
      {!noButtons && (
        <DialogActions sx={{ padding: 0 }}>
          {success && (
            <SuccessButton
              onClick={() => {
                handleSuccess()
              }}
            >
              {success}
            </SuccessButton>
          )}
          {success2 && (
            <SuccessButton
              onClick={() => {
                handleSuccess2()
              }}
            >
              {success2}
            </SuccessButton>
          )}
          {primary && (
            <PrimaryButton
              onClick={() => {
                handlePrimary()
              }}
            >
              {primary}
            </PrimaryButton>
          )}
          {error && (
            <ErrorButton
              onClick={() => {
                handleError()
              }}
            >
              {' '}
              {error}{' '}
            </ErrorButton>
          )}
          {/* {!cancel && (
            <Button
            color='error'
              onClick={() => {
                close()
              }}
            >
              Close
            </Button>
          )} */}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default Window
